import {
  ORCHESTRATION_WS_METHODS,
  type EnvironmentId as EnvironmentIdType,
  type OrchestrationGetThreadTimelineBeforeInput,
  type OrchestrationThreadTimelineWindow,
  type OrchestrationThreadStreamItem,
  type ThreadId as ThreadIdType,
} from "@t3tools/contracts";
import * as Cause from "effect/Cause";
import * as Effect from "effect/Effect";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import * as Stream from "effect/Stream";
import * as SubscriptionRef from "effect/SubscriptionRef";
import { AsyncResult, Atom } from "effect/unstable/reactivity";

import { EnvironmentRegistry } from "../connection/registry.ts";
import { connectionProjectionPhase } from "../connection/model.ts";
import { EnvironmentSupervisor } from "../connection/supervisor.ts";
import * as ConnectionWakeups from "../connection/wakeups.ts";
import { request, subscribeDynamic } from "../rpc/client.ts";
import { parseThreadKey, threadKey } from "./entities.ts";
import { logLiveRunDiagnostic } from "./liveRunDiagnostics.ts";
import { THREAD_STATE_IDLE_TTL_MS } from "./threadRetention.ts";
import {
  applyTimelineDelta,
  materializeTimelineWindow,
  mergeTimelineBeforeWindow,
} from "./timelineReducer.ts";
import { createEnvironmentCommand, followStreamInEnvironment } from "./runtime.ts";
import {
  EMPTY_ENVIRONMENT_THREAD_STATE,
  type EnvironmentThreadState,
  type EnvironmentThreadStatus,
} from "./threadState.ts";

function statusWithoutLiveData(state: EnvironmentThreadState): EnvironmentThreadStatus {
  return state.status === "deleted" ? "deleted" : "empty";
}

function formatThreadError(cause: Cause.Cause<unknown>): string {
  const error = Cause.squash(cause);
  return error instanceof Error && error.message.trim().length > 0
    ? error.message
    : "Could not synchronize the thread.";
}

/** Opaque backward paging request. The returned page is never persisted. */
export const loadEnvironmentThreadTimelineBefore = Effect.fn("EnvironmentThreadState.loadBefore")(
  function* (input: OrchestrationGetThreadTimelineBeforeInput) {
    return yield* request(ORCHESTRATION_WS_METHODS.getThreadTimelineBefore, input);
  },
);

export { mergeTimelineBeforeWindow };

export class ThreadTimelineBeforeUnavailableError extends Schema.TaggedErrorClass<ThreadTimelineBeforeUnavailableError>()(
  "ThreadTimelineBeforeUnavailableError",
  {
    threadId: Schema.String,
    reason: Schema.Literals(["not-live", "no-older-page"]),
  },
) {}

export class ThreadTimelineEpochMismatchError extends Schema.TaggedErrorClass<ThreadTimelineEpochMismatchError>()(
  "ThreadTimelineEpochMismatchError",
  {
    threadId: Schema.String,
    expectedEpoch: Schema.String,
    receivedEpoch: Schema.String,
  },
) {}

export const makeEnvironmentThreadState = Effect.fn("EnvironmentThreadState.make")(function* (
  threadId: ThreadIdType,
) {
  const supervisor = yield* EnvironmentSupervisor;
  const wakeups = yield* Effect.serviceOption(ConnectionWakeups.ConnectionWakeups);
  const state = yield* SubscriptionRef.make<EnvironmentThreadState>(EMPTY_ENVIRONMENT_THREAD_STATE);

  const setSynchronizing = SubscriptionRef.update(state, (current) =>
    current.status === "deleted"
      ? current
      : {
          ...current,
          status: "synchronizing" as const,
          error: Option.none(),
          workingStatus: null,
        },
  );
  const setDisconnected = SubscriptionRef.update(state, (current) => ({
    ...current,
    // Conversation windows are connection/process memory only. A disconnected
    // window is not presented as a resumable cache.
    data: Option.none(),
    timeline: Option.none(),
    status: statusWithoutLiveData(current),
    workingStatus: null,
  }));
  const setStreamError = (cause: Cause.Cause<unknown>) =>
    SubscriptionRef.update(state, (current) => ({
      ...current,
      data: Option.none(),
      timeline: Option.none(),
      status: statusWithoutLiveData(current),
      error: Option.some(formatThreadError(cause)),
      workingStatus: null,
    }));

  const setWindow = Effect.fn("EnvironmentThreadState.setWindow")(function* (
    item: Extract<OrchestrationThreadStreamItem, { kind: "tail" | "epoch-reset" }>,
  ) {
    const previous = yield* SubscriptionRef.get(state);
    yield* logLiveRunDiagnostic("thread.window", () => ({
      threadId,
      reason: item.kind,
      previousEpoch: Option.isSome(previous.timeline) ? previous.timeline.value.epoch : null,
      nextEpoch: item.window.epoch,
      previousStatus: previous.status,
      previousHadData: Option.isSome(previous.data),
      nextItemCount: item.window.items.length,
    }));
    const thread = materializeTimelineWindow(item.window);
    if (thread.deletedAt !== null) {
      yield* logLiveRunDiagnostic("thread.cleared", () => ({
        threadId,
        reason: "deleted-window",
        previousEpoch: Option.isSome(previous.timeline) ? previous.timeline.value.epoch : null,
        receivedEpoch: item.window.epoch,
      }));
      yield* SubscriptionRef.set(state, {
        data: Option.none(),
        timeline: Option.none(),
        status: "deleted",
        error: Option.none(),
        workingStatus: null,
      });
      return;
    }
    yield* SubscriptionRef.set(state, {
      data: Option.some({
        ...thread,
      }),
      timeline: Option.some(item.window),
      status: "live",
      error: Option.none(),
      workingStatus: null,
    });
  });

  const applyItem = Effect.fn("EnvironmentThreadState.applyItem")(function* (
    item: OrchestrationThreadStreamItem,
  ) {
    if (item.kind === "tail" || item.kind === "epoch-reset") {
      yield* setWindow(item);
      return;
    }

    const current = yield* SubscriptionRef.get(state);
    if (item.kind === "working-status") {
      if (
        Option.isSome(current.timeline) &&
        current.timeline.value.epoch === item.epoch &&
        current.timeline.value.threadId === item.threadId
      ) {
        yield* SubscriptionRef.set(state, { ...current, workingStatus: item.label });
      }
      return;
    }

    if (Option.isNone(current.timeline)) {
      yield* logLiveRunDiagnostic("thread.item-dropped", () => ({
        threadId,
        reason: "missing-current-window",
        itemKind: item.kind,
        receivedEpoch: item.delta.epoch,
        status: current.status,
      }));
      return;
    }
    const currentTimeline = current.timeline.value;
    yield* logLiveRunDiagnostic("thread.delta", () => ({
      threadId,
      currentEpoch: currentTimeline.epoch,
      receivedEpoch: item.delta.epoch,
      removedItemCount: item.delta.removedItems.length,
      upsertedItemCount: item.delta.upsertedItems.length,
      upsertedPartStates: item.delta.upsertedItems.flatMap((timelineItem) =>
        timelineItem.kind === "message-part"
          ? [
              {
                id: timelineItem.part.id,
                kind: timelineItem.part.kind,
                messageId: timelineItem.part.messageId,
                streaming:
                  timelineItem.part.kind === "text" || timelineItem.part.kind === "reasoning"
                    ? timelineItem.part.streaming
                    : null,
              },
            ]
          : [],
      ),
    }));
    const result = applyTimelineDelta(currentTimeline, item.delta);
    if (result.kind === "epoch-mismatch") {
      yield* logLiveRunDiagnostic("thread.cleared", () => ({
        threadId,
        reason: "epoch-mismatch",
        previousEpoch: currentTimeline.epoch,
        receivedEpoch: item.delta.epoch,
      }));
      yield* SubscriptionRef.set(state, {
        ...current,
        data: Option.none(),
        timeline: Option.none(),
        status: "synchronizing",
        workingStatus: null,
      });
      return yield* new ThreadTimelineEpochMismatchError({
        threadId,
        expectedEpoch: currentTimeline.epoch,
        receivedEpoch: item.delta.epoch,
      });
    }
    yield* SubscriptionRef.set(state, {
      ...current,
      data: Option.some({
        ...materializeTimelineWindow(result.window),
      }),
      timeline: Option.some(result.window),
      status: "live",
      error: Option.none(),
    });
  });

  yield* SubscriptionRef.changes(supervisor.state).pipe(
    Stream.runForEach((connectionState) => {
      switch (connectionProjectionPhase(connectionState)) {
        case "synchronizing":
        case "ready":
          return setSynchronizing;
        case "disconnected":
          return setDisconnected;
      }
    }),
    Effect.forkScoped,
  );

  const foregroundResubscriptions = Option.match(wakeups, {
    onNone: () => Stream.never,
    onSome: (service) =>
      service.changes.pipe(Stream.filter((reason) => reason === "application-active")),
  });

  yield* setSynchronizing;
  yield* Effect.forkScoped(
    Effect.forever(
      subscribeDynamic(
        ORCHESTRATION_WS_METHODS.subscribeThread,
        Effect.fn("EnvironmentThreadState.makeSubscribeInput")(function* (session) {
          yield* setSynchronizing;
          const includeWorkingStatus = yield* session.initialConfig.pipe(
            Effect.map((config) => config.threadWorkingStatusStream === true),
            Effect.orElseSucceed(() => false),
          );
          return {
            threadId,
            ...(includeWorkingStatus ? { includeWorkingStatus: true as const } : {}),
          };
        }),
        {
          onExpectedFailure: setStreamError,
          retryExpectedFailureAfter: "250 millis",
          resubscribe: foregroundResubscriptions,
          restartOnEnd: true,
        },
      ).pipe(
        Stream.runForEach(applyItem),
        Effect.catchTag("ThreadTimelineEpochMismatchError", () => setSynchronizing),
        Effect.delay("10 millis"),
      ),
    ),
  );

  return state;
});

export function threadStateChanges(environmentId: EnvironmentIdType, threadId: ThreadIdType) {
  return followStreamInEnvironment(
    environmentId,
    Stream.unwrap(makeEnvironmentThreadState(threadId).pipe(Effect.map(SubscriptionRef.changes))),
  );
}

export function createEnvironmentThreadStateAtoms<R, E>(
  runtime: Atom.AtomRuntime<EnvironmentRegistry | R, E>,
) {
  const liveFamily = Atom.family((key: string) => {
    const { environmentId, threadId } = parseThreadKey(key);
    return runtime
      .atom(threadStateChanges(environmentId, threadId), {
        initialValue: EMPTY_ENVIRONMENT_THREAD_STATE,
      })
      .pipe(
        Atom.setIdleTTL(THREAD_STATE_IDLE_TTL_MS),
        Atom.withLabel(`environment-thread-state:${key}`),
      );
  });

  const olderWindowFamily = Atom.family((key: string) =>
    Atom.make<Option.Option<OrchestrationThreadTimelineWindow>>(Option.none()).pipe(
      Atom.setIdleTTL(THREAD_STATE_IDLE_TTL_MS),
      Atom.withLabel(`environment-thread-older-window:${key}`),
    ),
  );

  const family = Atom.family((key: string) =>
    Atom.make((get) => {
      const live = get(liveFamily(key));
      const older = get(olderWindowFamily(key));
      return AsyncResult.map(live, (current) => {
        if (Option.isNone(current.timeline) || Option.isNone(older)) return current;
        const window = mergeTimelineBeforeWindow(current.timeline.value, older.value);
        return window === current.timeline.value
          ? current
          : {
              ...current,
              timeline: Option.some(window),
              data: Option.some({
                ...materializeTimelineWindow(window),
              }),
            };
      });
    }).pipe(
      Atom.setIdleTTL(THREAD_STATE_IDLE_TTL_MS),
      Atom.withLabel(`environment-thread-state-with-history:${key}`),
    ),
  );

  const loadOlder = createEnvironmentCommand(runtime, {
    label: "environment-thread-load-older",
    concurrency: {
      mode: "singleFlight",
      key: ({
        environmentId,
        input,
      }: {
        readonly environmentId: EnvironmentIdType;
        readonly input: { readonly threadId: ThreadIdType };
      }) => threadKey({ environmentId, threadId: input.threadId }),
    },
    execute: (input: { readonly threadId: ThreadIdType }, registry, environmentId) =>
      Effect.gen(function* () {
        const key = threadKey({ environmentId, threadId: input.threadId });
        const currentResult = registry.get(family(key));
        const current = Option.getOrUndefined(AsyncResult.value(currentResult));
        if (current === undefined || Option.isNone(current.timeline)) {
          return yield* new ThreadTimelineBeforeUnavailableError({
            threadId: input.threadId,
            reason: "not-live",
          });
        }
        const window = current.timeline.value;
        if (!window.hasOlder || window.beforeCursor === null) {
          return yield* new ThreadTimelineBeforeUnavailableError({
            threadId: input.threadId,
            reason: "no-older-page",
          });
        }
        const older = yield* loadEnvironmentThreadTimelineBefore({
          threadId: input.threadId,
          epoch: window.epoch,
          beforeCursor: window.beforeCursor,
        });
        const merged = mergeTimelineBeforeWindow(window, older);
        yield* Effect.sync(() => registry.set(olderWindowFamily(key), Option.some(merged)));
        return merged;
      }),
  });

  return {
    stateAtom: (environmentId: EnvironmentIdType, threadId: ThreadIdType) =>
      family(threadKey({ environmentId, threadId })),
    loadOlder,
  };
}

export * from "./archivedThreads.ts";
export * from "./checkpointDiff.ts";
export * from "./composerPathSearch.ts";
export * from "./threadCommands.ts";
export * from "./threadDetail.ts";
export * from "./liveRunDiagnostics.ts";
export * from "./threadShell.ts";
export * from "./threadState.ts";
export * from "./timelineReducer.ts";
